import {
    Directive,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
} from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { OrgNavigationService } from './org-nav.service';

import { inject } from '@angular/core';
import { RouterLink } from '@angular/router';

@Directive({
    selector: '[orgRouterLink]',
    standalone: true,
    hostDirectives: [
        {
            directive: RouterLink,
            inputs: ['queryParams', 'fragment', 'preserveFragment'],
        },
    ],
})
export class OrgRouterLinkDirective implements OnChanges {
    @Input() orgRouterLink: string | string[] = [];

    private routerLink = inject(RouterLink);
    private navService = inject(OrgNavigationService);

    ngOnChanges(): void {
        if (!this.orgRouterLink) return;

        // Build the final link using the navigation service
        const finalLink = this.navService.buildLink(this.orgRouterLink);

        // Check if the first part of the finalLink is a UUID, and if it matches, remove the duplicate
        if (finalLink.length > 1 && this.isUUID(finalLink[0])) {
            finalLink.shift(); // Remove the duplicated UUID
        }

        // ✅ Set it as a clean array — ensures Angular doesn't encode %2F
        this.routerLink.routerLink = [...finalLink];
    }

    // Helper method to check if a string is a UUID
    private isUUID(value: string): boolean {
        const uuidRegex =
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        return uuidRegex.test(value);
    }
}

@Directive({
    selector: '[orgRouterLinkActive]',
    standalone: true,
})
export class OrgRouterLinkActiveDirective implements OnInit, OnDestroy {
    @Input('orgRouterLinkActive') activeClass = 'router-link-active';

    private sub: Subscription | null = null;

    private router = inject(OrgNavigationService);
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private navService = inject(OrgNavigationService);
    private routerLink = inject(RouterLink, { optional: true });

    ngOnInit(): void {
        this.sub = this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                this.updateActiveState();
            });

        this.updateActiveState();
    }

    private updateActiveState(): void {
        if (!this.routerLink || this.routerLink.routerLink == null) return;

        const linkInput: any = this.routerLink.routerLink;
        const target = this.navService.buildLink(linkInput);
        const urlTree = this.router.createUrlTree(target);

        const isActive = this.router.isActive(urlTree, {
            paths: 'exact',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
        });

        if (isActive) {
            this.renderer.addClass(this.el.nativeElement, this.activeClass);
        } else {
            this.renderer.removeClass(this.el.nativeElement, this.activeClass);
        }
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }
}
